// src/stores/useTextStore.js
import { defineStore } from "pinia";
import * as textService from "@/services/textService";
import { useUserStore } from "@/stores/useUserStore";

export const useTextStore = defineStore("text", {
  state: () => ({
    texts: [],
    selectedTextIds: [],
    idText: null,
    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    searchTerm: "", // 🔍 novo campo para busca
    // 🔍 NOVO: lista de campos usados na busca
    textSearchFields: [
      "title",
      "subtitle",
      "author",
      "source",
      "category",
      "topics",
      "creator",
    ],

    // 🔥 NOVO: flag "Meus textos"
    myTextsOnly: false,

    // 🔍 NOVO: filtros por campo (painel "Filtros ativos" do TextForm).
    // Convivem com searchTerm/textSearchFields: cada campo aqui tem seu próprio
    // valor e combina em AND com os demais.
    filters: {
      title: "",
      subtitle: "",
      authorName: "",
      source: "",
      categoryName: "",
      topicName: "",
      creatorName: "",
      creatorId: "",
      textId: "",
      createdFrom: "",
      createdTo: "",
      permissionGroupName: "",
      permissionUserName: "",
    },

    shouldForcePageOne: false,
    selectedText: null, // <--- ESTE OBJETO VAI RECEBER A PROPRIEDADE 'allNotes'
    fileContent: "",
    // --- NOVO ESTADO: Adiciona as permissões do texto selecionado ---
    textPermissions: [],
  }),

  getters: {
    isAllSelected: (state) => (total) => {
      return state.selectedTextIds.length === total;
    },
    // currentText é um getter que retorna um item da lista 'texts'
    // Ele não será o local para 'allNotes', mas sim 'selectedText'
    currentText(state) {
      return state.texts.find((t) => t.textId === state.idText) || null;
    },
  },

  actions: {
    // 🔥 helper opcional p/ clareza
    setMyTextsOnly(value) {
      this.myTextsOnly = value;
    },

    // 🔍 NOVO: filtros por campo
    setFilter(field, value) {
      if (!Object.prototype.hasOwnProperty.call(this.filters, field)) return;
      this.filters[field] = value ?? "";
    },

    clearFilters() {
      Object.keys(this.filters).forEach((k) => {
        this.filters[k] = "";
      });
      this.myTextsOnly = false;
    },

    setIdText(id) {
      this.idText = id;
    },

    toggleSelection(id) {
      const index = this.selectedTextIds.indexOf(id);
      if (index > -1) {
        this.selectedTextIds.splice(index, 1);
      } else {
        this.selectedTextIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedTextIds = [...ids];
    },

    clearSelection() {
      this.selectedTextIds = [];
    },

    async fetchTexts() {
      try {
        const response = await textService.fetchTexts();
        this.texts = response.data;
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error("❌ Erro ao buscar textos:", this.error);
      }
    },

    async getTextById(id) {
      try {
        // 1. Busca os metadados do texto a partir do seu ID
        const response = await textService.getTextById(id);
        this.selectedText = response.data;
        this.idText = id;

        // A lógica de buscar 'allNotes' foi REMOVIDA,
        // pois a responsabilidade de gerenciar notas agora pertence à `useActivityStore`.

        // 2. Busca o conteúdo do arquivo .txt associado ao texto
        if (this.selectedText?.filepath) {
          //alert(this.wwwroot + this.selectedText.filepath);

          await this.fetchTextFileContent(this.selectedText.filepath);
        } else {
          console.warn(
            "filepath não encontrado no objeto 'selectedText' para buscar conteúdo do arquivo."
          );
          this.fileContent = ""; // Garante que o conteúdo seja limpo se não houver arquivo
        }

        // 3. Atualiza a lista local 'texts' (usada para a exibição paginada)
        const index = this.texts.findIndex((t) => t.textId === id);

        if (index === -1) {
          // Se o texto não estiver na lista atual, adiciona-o
          this.texts.push(this.selectedText);
        } else {
          // Se já estiver, atualiza-o com os dados mais recentes
          this.texts[index] = this.selectedText;
        }

        return response.data;
      } catch (error) {
        const mensagem = error.response?.data || error.message;
        console.error("❌ Erro ao obter texto:", mensagem);
        throw new Error(mensagem);
      }
    },

    async createText(data, useElastic = false) {
      //useElastic é um parâmetro opcional, e se não for informado quando a função for chamada, o valor padrão será false.
      try {
        console.time("createText");
        //console.log("data: ", JSON.stringify(data));
        //alert("data: ", data);
        const response = await textService.createText(data, useElastic);
        //alert('createText2');
        console.timeEnd("createText");

        // 💥 Sincroniza contadores do usuário
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();

        this.shouldForcePageOne = true;
        console.time("fetchPaged");
        await this.fetchTextsPaged(
          1,
          this.pageSize,
          this.searchTerm,
          this.textSearchFields
        );

        console.timeEnd("fetchPaged");

        return response.data; // retorna o texto criado
      } catch (error) {
        console.error(
          "❌ Erro ao cadastrar texto na store:",
          error.response?.data || error.message || error
        );
        // 'throw error' garante que o objeto de erro completo
        // seja enviado ao componente, permitindo que o 'alert' exiba a mensagem da API.
        throw error;
      }
    },

    async updateText(data, useElastic = false) {
      try {
        console.log(
          "Permissões a serem enviadas (useTextStore):",
          data.permissions
        );
        const response = await textService.updateText(data, useElastic);
        const payload = response.data; // << TextEditResultDto ou objeto com { text, ... }

        // 💥 Sincroniza contadores do usuário
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();

        // Se o backend devolve o texto em payload.text, usamos; senão, usamos o próprio payload
        const updatedText = payload.text ?? payload;

        // MESCLAR, e não substituir.
        //
        // O que o PUT devolve é um TextReadDto, mais pobre que o TextDetailDto
        // que o GET /texts/{id} entrega e que o leitor tem em mãos: não traz
        // `contentFormat` nem `createdByUserName`. Substituindo, esses campos
        // sumiam — e `contentFormat` ausente vale "plaintext" para quem decide o
        // renderizador (`isPlaintextContent`, no TextDisplayer). Resultado: ao
        // salvar o conteúdo, um texto HTML passava por texto puro e a tela
        // piscava o fonte com as tags à mostra até a releitura chegar.
        //
        // Mesclando, o PUT atualiza o que sabe e cala sobre o que não conhece.
        // Campo limpo de propósito continua sendo limpo: ele vem no DTO, com
        // null, e vence na mesclagem.
        //
        // A confirmação de impacto nas notas responde com outro formato (sem
        // `text` e sem `title`); ela é decidida por quem chamou, e não tem nada
        // que encostar no texto guardado aqui.
        const isTextPayload = updatedText && updatedText.title !== undefined;

        if (isTextPayload) {
          const index = this.texts.findIndex(
            (t) => t.textId === updatedText.textId
          );
          if (index !== -1) {
            this.texts[index] = { ...this.texts[index], ...updatedText };
          }

          // Atualiza também o texto selecionado, se for o caso
          if (
            this.selectedText &&
            this.selectedText.textId === updatedText.textId
          ) {
            this.selectedText = { ...this.selectedText, ...updatedText };
          }
        }

        // 👇 ESSENCIAL: devolve o payload, para o componente saber se houve notas afetadas
        return payload;
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar texto na store:",
          error.response?.data || error.message || error
        );
        throw error;
      }
    },

    // Edição de campos avulsos a partir do painel do leitor (título, subtítulo,
    // autor, categoria, assuntos, fonte). Recebe só o que mudou; o resto vem do
    // servidor.
    //
    // Três cuidados, e nenhum é zelo:
    //
    //   1. A releitura ANTES de gravar é obrigatória. O PUT /texts/{id} é
    //      substituição: o serviço APAGA todas as permissões de usuário e de
    //      grupo do texto antes de regravar as que vierem no payload. Mandar só
    //      o campo editado tiraria o compartilhamento do texto sem erro nenhum.
    //
    //   2. NADA de `pastedContent` nem de `file`. O backend só mexe no conteúdo
    //      quando um deles chega — e mexer no conteúdo dispara a análise de
    //      impacto nas notas, que responde 409 pedindo confirmação. Editar um
    //      subtítulo não pode abrir essa porta.
    //
    //      `coverBlob` e `removeCover` também não entram por conta própria, mas
    //      podem vir no `patch` quando o usuário mexeu na capa: ausentes,
    //      significam "não mexi", e é isso que preserva a capa gravada — a mesma
    //      regra que o EditTextForm documenta.
    //
    //   3. A leitura usa o textService direto, e não `getTextById`: aquele
    //      rebaixa o arquivo do texto inteiro junto e o reescreve em
    //      `fileContent` — o conteúdo que o leitor está exibindo neste instante.
    //
    // Ao fim, relê os metadados para devolver ao `selectedText` o DTO COMPLETO.
    // O que o PUT responde não traz `createdByUserName` nem as permissões, e o
    // painel ficaria sem o nome de quem inseriu o texto.
    async updateTextFields(textId, patch = {}) {
      const { data: current } = await textService.getTextById(textId);
      if (!current) {
        throw new Error(`Texto ${textId} não encontrado.`);
      }

      const grants = await this.fetchTextPermissions(textId);

      await this.updateText({
        textId: current.textId,
        title: current.title,
        subtitle: current.subtitle ?? "",
        synopsis: current.synopsis ?? "",
        source: current.source ?? "",
        active: current.active,
        authorId: current.authorId ?? 0,
        categoryId: current.categoryId ?? 0,
        topicIds: Array.isArray(current.topicIds) ? current.topicIds : [],
        permissions: (grants || []).map((p) => ({
          entityId: p.userId ?? p.groupId,
          permissionId: p.permissionId,
          type: p.userId != null ? "user" : "group",
        })),
        ...patch,
      });

      const { data: refreshed } = await textService.getTextById(textId);
      if (refreshed) {
        this.selectedText = refreshed;
        const index = this.texts.findIndex((t) => t.textId === textId);
        if (index !== -1) this.texts[index] = refreshed;
      }

      return refreshed;
    },

    // ✅ NOVA ACTION PARA ATUALIZAR O STATUS DO TEXTO
    async updateTextStatus({ textId, active }) {
      try {
        await textService.updateTextStatus(textId, active);

        // Atualiza o estado local para refletir a mudança sem precisar recarregar a lista
        const index = this.texts.findIndex((t) => t.textId === textId);
        if (index !== -1) {
          this.texts[index].active = active;
        }

        // Se o texto atualmente selecionado for o que mudou, atualiza ele também
        if (this.selectedText && this.selectedText.textId === textId) {
          this.selectedText.active = active;
        }
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar o status do texto:",
          error.response?.data || error.message
        );
        throw error;
      }
    },

    async deleteTextById(id, useElastic = false) {
      try {
        await textService.deleteText(id, useElastic);

        // 💥 Sincroniza contadores do usuário
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();

        await this.fetchTextsPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
          this.textSearchFields
        );
        if (this.texts.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchTextsPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
            this.textSearchFields
          );
        }
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir texto:", serverMessage);

        if (serverMessage.includes("REFERENCES `activities` (`activity_id`)")) {
          throw new Error(
            "Não é possível excluir o texto, pois ele contém atividades associadas. Remova as atividades do texto antes de tentar excluí-lo."
          );
        }

        this.error = serverMessage;
        throw new Error(serverMessage);
      }
    },

    async deleteSelectedTexts(useElastic = false) {
      if (!this.selectedTextIds.length) return;

      try {
        //alert('deleteSelectedTexts:' + this.selectedTextIds);
        await textService.deleteTextsBatch(this.selectedTextIds, useElastic);

        // 💥 Sincroniza contadores do usuário
        const userStore = useUserStore();
        await userStore.refreshCurrentUser();

        await this.fetchTextsPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
          this.textSearchFields
        );
        if (this.texts.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchTextsPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
            this.textSearchFields
          );
        }
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir textos em lote:", serverMessage);

        if (serverMessage.includes("REFERENCES `activities` (`activity_id`)")) {
          throw new Error(
            "Não é possível excluir um ou mais textos selecionados, pois eles contêm atividades associadas. Remova as atividades dos textos antes de tentar excluí-los."
          );
        }

        this.error = serverMessage;
        throw new Error(serverMessage);
      }
    },

    async fetchTextsPaged(pageNumber, pageSize, search, searchFields) {
      try {
        const DEFAULT_FIELDS = [
          "title",
          "subtitle",
          "author",
          "source",
          "category",
          "topics",
          "creator",
        ];

        // garante que o estado da store fique sincronizado
        this.currentPage = pageNumber;
        this.pageSize = pageSize;

        if (typeof search === "string") {
          this.searchTerm = search;
        }

        // se vier lista de campos da chamada, atualiza o estado
        // (inclusive lista vazia, usada pelo "Limpar seleção")
        if (Array.isArray(searchFields)) {
          this.textSearchFields = [...searchFields];
        }

        // fallback: só se for null/undefined, não se estiver []
        if (!Array.isArray(this.textSearchFields)) {
          this.textSearchFields = [...DEFAULT_FIELDS];
        }

        const response = await textService.fetchTextsPaged(
          pageNumber,
          pageSize,
          this.searchTerm,
          this.textSearchFields, // 🔥 envia também os campos (pode ser [])
          this.myTextsOnly,      // 👈 novo argumento
          this.filters           // 🔍 filtros por campo
        );

        this.texts = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
      } catch (error) {
        const mensagem = error.response?.data || error.message;
        this.error = mensagem;
        console.error("❌ Erro ao buscar textos paginados:", mensagem);
        throw new Error(mensagem);
      }
    },

    setSearchTerm(term) {
      this.searchTerm = term;
      this.currentPage = 1; // 🔁 redefine a página
      this.shouldForcePageOne = true; // 🔁 aciona o highlight no BottomPagination
      this.fetchTextsPaged(1, this.pageSize, term, this.textSearchFields); // 🔍 dispara a busca
    },

    async fetchTextFileContent(filepath) {
      try {
        const content = await textService.getFileContent(filepath);
        //alert(this.wwwroot + filepath);
        // *** CORREÇÃO CRÍTICA AQUI: Normalizar o conteúdo para usar apenas \n ***
        this.fileContent = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
        return this.fileContent;
      } catch (error) {
        console.error(
          "❌ Erro ao buscar conteúdo do arquivo .txt:",
          error.message
        );
        this.fileContent = "";
        throw new Error("Não foi possível carregar o conteúdo do arquivo.");
      }
    },

    // --- AÇÕES DE PERMISSÃO PARA TEXTOS (NOVAS) ---
    async fetchTextPermissions(textId) {
      try {
        const response = await textService.fetchTextPermissions(textId);
        this.textPermissions = response.data;
        return response.data;
      } catch (error) {
        console.error("❌ Erro ao buscar permissões do texto:", error.message);
        this.textPermissions = [];
        throw error;
      }
    },

    async addTextPermission(textId, permissionData) {
      try {
        await textService.addTextPermission(textId, permissionData);
        await this.fetchTextPermissions(textId);
      } catch (error) {
        console.error(
          "❌ Erro ao adicionar permissão no texto:",
          error.message
        );
        throw error;
      }
    },

    async removeTextPermission(textId, userId, permissionId) {
      try {
        await textService.removeTextPermission(textId, userId, permissionId);
        await this.fetchTextPermissions(textId);
      } catch (error) {
        console.error("❌ Erro ao remover permissão do texto:", error.message);
        throw error;
      }
    },
  },
});
